import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { announcements as announcementsApi } from "../api/endpoints";
import type { Announcement, User } from "../types";
import AnnouncementModal from "./AnnouncementModal";

interface Props {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
}

type NavItem = {
  path: string;
  label: string;
  hint: string;
  icon: string;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

function getPersonaConfig(user: User) {
  const userRole = user.role?.name || "";
  const adminRoles = ["SuperAdmin", "Admin"];
  const shopperRoles = ["Mystery Shopper"];
  const guestRoles = ["Guest"];
  const clientRoles = [
    "Client Account Executive",
    "Regional Representative",
    "Client Property Manager",
    "Director of Sales",
    "Director of Catering",
    "Client Sales Staff Individual",
  ];

  const isAdmin = adminRoles.includes(userRole) || user.is_staff || user.is_superuser;
  const isShopper = shopperRoles.includes(userRole) && !isAdmin;
  const isGuest = guestRoles.includes(userRole);
  const isRequestor = clientRoles.includes(userRole);
  const hasReportDashboardAccess = Boolean(user.report_management_company_id);

  const personaLabel = isAdmin
    ? "Admin Workspace"
    : isGuest || hasReportDashboardAccess
    ? "Portal Reports"
    : isShopper
    ? "Caller Workspace"
    : "Client Workspace";

  const callerGroups: NavGroup[] = [
    {
      title: "My Work",
      items: [
        { path: "/", label: "Home", hint: "Overview", icon: "H" },
        { path: "/calls", label: "My Calls", hint: "Queue", icon: "C" },
        ...(isRequestor
          ? [
              { path: "/orders", label: "My Orders", hint: "Orders", icon: "O" },
              { path: "/purchase", label: "Purchase Calls", hint: "New order", icon: "P" },
              { path: "/reports", label: "Reports", hint: "Exports", icon: "R" },
            ]
          : []),
      ],
    },
    {
      title: "Account",
      items: [
        { path: "/announcements", label: "Announcements", hint: "News", icon: "A" },
        { path: "/profile", label: "My Profile", hint: "Account", icon: "P" },
      ],
    },
  ];

  const adminGroups: NavGroup[] = [
    {
      title: "Operations",
      items: [
        { path: "/", label: "Admin Home", hint: "Today", icon: "H" },
        { path: "/calls", label: "Calls Desk", hint: "Queue", icon: "C" },
        { path: "/orders", label: "Orders", hint: "Billing", icon: "O" },
        { path: "/purchase", label: "Purchase Calls", hint: "Onboard", icon: "P" },
      ],
    },
    {
      title: "Reports",
      items: [
        { path: "/reports", label: "Reports", hint: "Exports", icon: "R" },
        { path: "/management-dashboard", label: "Portal Dashboard", hint: "Portal", icon: "D" },
      ],
    },
    {
      title: "Setup",
      items: [
        { path: "/properties", label: "Properties", hint: "Hotels", icon: "T" },
        { path: "/management-companies", label: "Management Companies", hint: "HMC", icon: "M" },
        { path: "/contract-specs", label: "Contract Specs", hint: "Contracts", icon: "K" },
        { path: "/call-setup", label: "Call Setup", hint: "Levels & rates", icon: "L" },
        { path: "/survey-builder", label: "Survey Builder", hint: "Surveys", icon: "Q" },
        { path: "/imported-calls", label: "Imported Calls", hint: "Excel", icon: "I" },
        { path: "/shoppers", label: "Callers / Shoppers", hint: "People", icon: "S" },
        { path: "/users", label: "Admin Users", hint: "Users", icon: "U" },
        { path: "/user-passwords", label: "User Passwords", hint: "Access", icon: "P" },
        { path: "/user-activity", label: "User Activity", hint: "Online", icon: "V" },
      ],
    },
    {
      title: "Admin",
      items: [
        { path: "/announcements", label: "Announcements", hint: "News", icon: "A" },
        { path: "/profile", label: "My Profile", hint: "Account", icon: "P" },
      ],
    },
  ];

  const guestGroups: NavGroup[] = [
    {
      title: "Reports",
      items: [{ path: "/management-dashboard", label: "Portal Dashboard", hint: "HMC", icon: "R" }],
    },
    {
      title: "Account",
      items: [{ path: "/profile", label: "My Profile", hint: "Account", icon: "P" }],
    },
  ];

  return {
    personaLabel,
    visibleGroups: isAdmin ? adminGroups : isGuest || hasReportDashboardAccess ? guestGroups : callerGroups,
  };
}

export default function Layout({ user, onLogout, children }: Props) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const [announcementItems, setAnnouncementItems] = useState<Announcement[]>([]);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const userRole = user.role?.name || "Workspace";
  const isGuest = userRole === "Guest";
  const hasReportDashboardAccess = Boolean(user.report_management_company_id);
  const { personaLabel, visibleGroups } = getPersonaConfig(user);
  const flatItems = visibleGroups.flatMap((group) => group.items);
  const matchesPath = (path: string) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));
  const currentItem = flatItems.find((item) => matchesPath(item.path));
  const currentGroup =
    visibleGroups.find((group) => group.items.some((item) => matchesPath(item.path))) || visibleGroups[0];
  const isPortalPage = location.pathname.startsWith("/management-dashboard");

  React.useEffect(() => {
    if (isGuest || hasReportDashboardAccess) {
      setAnnouncementItems([]);
      setShowAnnouncements(false);
      return;
    }

    let isActive = true;

    announcementsApi
      .list()
      .then((items) => {
        if (!isActive) return;
        const sorted = [...items].sort((left, right) => right.priority - left.priority);
        setAnnouncementItems(sorted);
        setShowAnnouncements(sorted.length > 0);
      })
      .catch(() => {
        if (!isActive) return;
        setAnnouncementItems([]);
        setShowAnnouncements(false);
      });

    return () => {
      isActive = false;
    };
  }, [isGuest, hasReportDashboardAccess, user.id]);

  return (
    <div className={`app-shell ${isPortalPage ? "is-portal-page" : ""}`}>
      {showAnnouncements && (
        <AnnouncementModal items={announcementItems} onClose={() => setShowAnnouncements(false)} />
      )}

      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-slate-950/35 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`app-sidebar ${sidebarOpen ? "is-open" : ""} ${sidebarCollapsed ? "is-collapsed" : ""}`}>
        <div className="sidebar-brand">
          <div className="brand-mark">SC</div>
          <div className="relative sidebar-brand-copy">
            <div className="eyebrow text-emerald-100/70">{personaLabel}</div>
            <div className="text-xl font-semibold tracking-tight">ShepCRM</div>
          </div>
          <button
            type="button"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() =>
              setSidebarCollapsed((current) => {
                const next = !current;
                try {
                  localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
                } catch {}
                return next;
              })
            }
            className="sidebar-collapse-button hidden lg:inline-flex"
          >
            {sidebarCollapsed ? ">" : "<"}
          </button>
        </div>

        <nav className="sidebar-nav">
          {visibleGroups.map((group) => (
            <div key={group.title} className="nav-group">
              <div className="nav-group-title">{group.title}</div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = matchesPath(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-link ${active ? "nav-link-active" : ""}`}
                      title={`${item.label} • ${item.hint}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className={`nav-icon ${active ? "nav-icon-active" : ""}`}>{item.icon}</span>
                      <div className="nav-copy min-w-0">
                        <div className="nav-label text-sm font-medium">{item.label}</div>
                        <div className="nav-meta">{item.hint}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="label-caps text-emerald-100/65">Active Profile</div>
          <div className="sidebar-footer-name mt-2 text-base font-semibold">
            {user.first_name} {user.last_name}
          </div>
          <div className="sidebar-footer-email mt-1 text-sm text-slate-300/72">{user.email}</div>
          <div className="sidebar-footer-actions mt-4 flex flex-wrap gap-2">
            <Link to="/profile" className="button-ghost !px-4 !py-2 !text-sm">
              <span className="sidebar-action-full">Profile</span>
              <span className="sidebar-action-short">P</span>
            </Link>
            <button type="button" onClick={onLogout} className="button-secondary !px-4 !py-2 !text-sm">
              <span className="sidebar-action-full">Logout</span>
              <span className="sidebar-action-short">L</span>
            </button>
          </div>
        </div>
      </aside>

      <div className={`app-main ${sidebarCollapsed ? "is-collapsed" : ""}`}>
        <header className="topbar">
          <div className="topbar-main">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="button-ghost !px-4 !py-2 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                Menu
              </button>
              <div className="min-w-0">
                <div className="eyebrow">{currentGroup?.title || "Workspace"}</div>
                <div className="truncate text-lg font-semibold tracking-tight text-slate-900">
                  {currentItem?.label || "ShepCRM"}
                </div>
                <div className="topbar-context">{currentItem?.hint || personaLabel}</div>
              </div>
            </div>

            <div className="topbar-actions">
              <div className="badge-pill bg-teal-900/90 text-white">{personaLabel}</div>
              <div className="rounded-full border border-slate-200/70 bg-white/70 px-4 py-2 text-sm text-slate-600">
                {userRole}
              </div>
              <Link to="/profile" className="button-ghost hidden sm:inline-flex">
                Profile
              </Link>
              <button type="button" onClick={onLogout} className="button-secondary hidden sm:inline-flex">
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 pb-10 pt-6 md:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
